import { useEffect, useRef } from 'react';

import { AUTH } from '@/config/constants';
import { env } from '@/config/env';
import { setCredentials, logout } from '@/features/auth/stores/authSlice';
import { trySilentSessionRestore } from '@/features/auth/utils/silentSessionRestore';
import { getAccessTokenExpiryMs, isTokenExpired } from '@/features/auth/utils/token';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';

/**
 * Lên lịch `POST /api/auth/refresh` trước khi access JWT hết hạn (cookie refresh vẫn hợp lệ).
 * Trùng payload với bootstrap / interceptor — không tạo luồng refresh thứ hai.
 */
export function useProactiveSessionRefresh(): void {
  const dispatch = useAppDispatch();
  const { isAuthenticated, token } = useAppSelector((s) => s.auth);
  const runIdRef = useRef(0);

  useEffect(() => {
    if (!env.API_BASE_URL || !isAuthenticated || !token) return;
    if (isTokenExpired(token)) return;

    const expMs = getAccessTokenExpiryMs(token);
    if (expMs == null) return;

    const refreshAt = expMs - AUTH.PROACTIVE_REFRESH_BEFORE_EXP_MS;
    const delay = Math.max(0, refreshAt - Date.now());
    const id = ++runIdRef.current;
    let cancelled = false;

    const timer = window.setTimeout(async () => {
      if (cancelled || id !== runIdRef.current) return;
      const restored = await trySilentSessionRestore();
      if (cancelled || id !== runIdRef.current) return;
      if (restored) {
        dispatch(setCredentials(restored));
      } else {
        dispatch(logout());
      }
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [dispatch, isAuthenticated, token]);
}
